import { Modal, PixelIcon } from '@/components/ui';
import { useShop } from '@/contexts/ShopContext';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { getMenuHapticPattern } from '@/utils/haptics';
import { FONT, FONT_SIZE, SPACING, COLOR, actionButtonStyle, statCardStyle, countdownStyle } from '@/config/designTokens';
import { motion } from 'motion/react';
import React, { useEffect, useState } from 'react';

interface GameOverModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRestart: () => void;
  onHome: () => void;
  onShop: () => void;
  stats: {
    level: number;
    score: number;
    steps: number;
  };
}

// Action button matching MinimalHUD pause menu pattern exactly
function ActionButton({
  label,
  iconName,
  iconColor,
  onClick,
}: {
  label: string;
  iconName: Parameters<typeof PixelIcon>[0]['name'];
  iconColor: string;
  onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.96 }}
      onClick={onClick}
      style={actionButtonStyle()}
    >
      <PixelIcon name={iconName} size={18} color={iconColor} />
      {label}
    </motion.button>
  );
}

const GameOverModal: React.FC<GameOverModalProps> = ({
  isOpen,
  onClose: _,
  onRestart,
  onHome,
  onShop,
  stats,
}) => {
  const { shopState } = useShop();
  const { pattern } = useHapticFeedback();
  const [countdown, setCountdown] = useState(10);
  const [hasTriggeredOpenFeedback, setHasTriggeredOpenFeedback] = useState(false);

  // Auto close after 10 seconds with countdown
  useEffect(() => {
    if (isOpen) {
      setCountdown(10);
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            onHome();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isOpen, onHome]);

  useEffect(() => {
    if (!isOpen) {
      setHasTriggeredOpenFeedback(false);
      return;
    }
    if (hasTriggeredOpenFeedback) return;
    pattern(getMenuHapticPattern('error'));
    setHasTriggeredOpenFeedback(true);
  }, [hasTriggeredOpenFeedback, isOpen, pattern]);

  const handleClose = () => {
    onHome();
  };

  const getCountdownColor = () => {
    if (countdown <= 3) return COLOR.dangerLight;
    if (countdown <= 6) return COLOR.warningLight;
    return COLOR.successLight;
  };

  const darkStatCard = (accentColor: string): React.CSSProperties => ({
    ...statCardStyle(accentColor),
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    border: `2px solid color-mix(in srgb, ${accentColor} 35%, transparent)`,
  });

  const statLabelStyle: React.CSSProperties = {
    fontSize: FONT_SIZE.statLabel,
    color: COLOR.textSecondary,
    marginBottom: '2px',
    fontFamily: FONT.body,
  };

  const statValueStyle = (color: string): React.CSSProperties => ({
    fontSize: FONT_SIZE.statValue,
    fontWeight: 'bold',
    color,
    fontFamily: FONT.body,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Game Over"
      subtitle="Out of lives"
      variant="danger"
      size="medium"
      showCloseButton={false}
      closeOnBackdrop={false}
      closeOnEscape={false}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: SPACING.md,
          marginBottom: SPACING.lg,
          width: '100%',
        }}>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={darkStatCard(COLOR.statRed)}
          >
            <div style={{ marginBottom: SPACING.xs }}>
              <PixelIcon name="levels" size={24} color={COLOR.statRed} />
            </div>
            <div style={statLabelStyle}>Level</div>
            <div style={statValueStyle(COLOR.statRed)}>{stats.level + 1}</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            style={darkStatCard(COLOR.statOrange)}
          >
            <div style={{ marginBottom: SPACING.xs }}>
              <PixelIcon name="star" size={24} color={COLOR.statOrange} />
            </div>
            <div style={statLabelStyle}>Score</div>
            <div style={statValueStyle(COLOR.statOrange)}>{stats.score.toLocaleString()}</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            style={darkStatCard(COLOR.statBlue)}
          >
            <div style={{ marginBottom: SPACING.xs }}>
              <PixelIcon name="steps" size={24} color={COLOR.statBlue} />
            </div>
            <div style={statLabelStyle}>Steps</div>
            <div style={statValueStyle(COLOR.statBlue)}>{stats.steps}</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            style={darkStatCard(COLOR.statYellow)}
          >
            <div style={{ marginBottom: SPACING.xs }}>
              <PixelIcon name="coin" size={24} color={COLOR.statYellow} />
            </div>
            <div style={statLabelStyle}>Coins</div>
            <div style={statValueStyle(COLOR.statYellow)}>{shopState.coins}</div>
          </motion.div>
        </div>

        {/* Action Buttons -- same pattern as pause menu */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: SPACING.md,
          width: '100%',
          maxWidth: 320,
          marginBottom: SPACING.lg,
        }}>
          <ActionButton
            label="Try Again"
            iconName="retry"
            iconColor={COLOR.actionResume}
            onClick={onRestart}
          />
          <ActionButton
            label="Visit Shop"
            iconName="shop"
            iconColor={COLOR.buttonBlue}
            onClick={onShop}
          />
          <ActionButton
            label="Home"
            iconName="home"
            iconColor={COLOR.actionQuit}
            onClick={onHome}
          />
        </div>

        {/* Countdown */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          style={{ ...countdownStyle(getCountdownColor()), width: '100%', maxWidth: 320 }}
        >
          <p style={{
            fontSize: FONT_SIZE.countdownLabel,
            color: COLOR.textSecondary,
            margin: 0,
            fontFamily: FONT.body,
          }}>
            Home in
          </p>
          <motion.span
            key={countdown}
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            style={{
              fontSize: FONT_SIZE.countdown,
              fontWeight: 'bold',
              color: getCountdownColor(),
              fontFamily: FONT.body,
            }}
          >
            {countdown}s
          </motion.span>
        </motion.div>
      </div>
    </Modal>
  );
};

export default GameOverModal;
